import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { debounce } from 'lodash';

import { InfoButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';

import * as api from '@test_prep/app/api';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  onDismiss: () => void;
  rating: {
    rateableId: number;
    rateableType: string;
    rating?: number;
    text?: string;
  };
  tutoringSessionId: number;
}

const RatingCollection: React.FC<Props> = (props) => {
  const textAreaRef = useRef(null);
  const [rating, setRating] = useState<number>(props.rating?.rating);
  const [ratingText, setRatingText] = useState<string>(props.rating?.text);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(!!props.rating?.rating);

  const debouncedSaveRating = useRef(
    debounce((values) => {
      saveRating(values);
    }, 2000, { trailing: true })
  );

  useEffect(() => {
    if (!rating) return;

    textAreaRef.current?.focus();
    saveRating({ rating });
  }, [rating]);

  useEffect(() => {
    if (!hasSubmitted) return;

    const dismissTimeout = setTimeout(props.onDismiss, 3000);

    return () => clearTimeout(dismissTimeout);
  }, [hasSubmitted]);

  const handleRatingTextChanged = (e) => {
    setRatingText(e.target.value);
    debouncedSaveRating.current({ rating, text: e.target.value });
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    saveRating({ rating, text: ratingText });
  };

  const saveRating = (values) => {
    api.createOrUpdateRating({
      rateableId: props.rating.rateableId,
      rateableType: props.rating.rateableType,
      rating: values
    });
  };

  if (hasSubmitted) {
    return (
      <div
        className={cx(style.fadeOut, 'd-flex flex-column gap-s u-padding-A-s')}
        style={{
          backgroundColor: colors.alertInfoBg,
          borderRadius: 8,
          color: colors.brandInfo,
          opacity: 0
        }}
      >
        <div className="align-items-center d-flex gap-s small">
          <div className="flex-1">
            Thanks for your feedback!
          </div>

          <div onClick={props.onDismiss} style={{ cursor: 'pointer', padding: 7 }}>
            <FontAwesomeIcon icon="close" />
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div
        className={cx(style.fadeIn, 'd-flex flex-column gap-s u-padding-A-s')}
        style={{
          backgroundColor: colors.alertInfoBg,
          borderRadius: 8,
          color: colors.brandInfo
        }}
      >
        <div className="d-flex gap-s small">
          <div className="d-flex flex-1 flex-column gap-s">
            Did you like this tutoring session?

            <div className="d-flex gap-s">
              {
                (!rating || rating === 1) && (
                  <div>
                    <a className="text-no-decoration" onClick={() => setRating(1)}>
                      <FontAwesomeIcon icon={rating === 1 ? 'check' : 'thumbs-o-up'} /> Yes
                    </a>
                  </div>
                )
              }

              {
                (!rating || rating === -1) && (
                  <div>
                    <a className="text-no-decoration" onClick={() => setRating(-1)}>
                      <FontAwesomeIcon icon={rating === 1 ? 'check' : 'thumbs-o-down'} /> No
                    </a>
                  </div>
                )
              }
            </div>
          </div>

          <div onClick={props.onDismiss} style={{ cursor: 'pointer', marginTop: '-7px', padding: 7 }}>
            <FontAwesomeIcon icon="close" />
          </div>
        </div>

        {
          rating && (
            <div className="d-flex flex-column gap-xs">
              <small>
                <strong>
                  {rating === 1 ? 'What did you like?' : 'How could we do better?'}
                </strong>
              </small>

              <textarea
                onChange={handleRatingTextChanged}
                ref={textAreaRef}
                style={{
                  backgroundColor: '#E6F7FF',
                  border: `1px dashed ${colors.brandInfo}`,
                  borderRadius: 8,
                  color: colors.brandInfo,
                  resize: 'none'
                }}
              />

              <InfoButton
                className="u-margin-T-s"
                onClick={handleSubmit}
                size="sm"
              >
                Submit Feedback
              </InfoButton>
            </div>
          )
        }
      </div>
    );
  }
};

export default RatingCollection;
